import { OrderTableSvgIcon } from '@/assets/svg'
import { ReusableTable } from '@/components'
import { AvatarNameComp, StatusBadge } from '@/components/fragments'
import type { TableAction, TableColumn } from '@/components/table'
import { useGetAdminFiltersQuery } from '@/redux/services/auth/authApi'
import { useGetCustomerListQuery } from '@/redux/services/customer'
import type { Dictionary } from '@/types'
import useDebounce from '@/utils/hooks/useDebounce'
import routesPath from '@/utils/routesPath'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const { CUSTOMERS } = routesPath

export default function CustomerListPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(15)
  const [filterQuery, setFilterQuery] = useState({
    location: '',
    status: '',
    sort_by: 'created_at',
    sort_order: 'desc',
  })

  const columns: TableColumn<Dictionary>[] = [
    {
      key: 'serial',
      header: 'S/N',
      accessor: 'id',
      sortable: false,
      width: '60px',
      render: (_, item) => {
        const currentData = data?.customers || []
        const itemIndex = currentData.findIndex(
          (customer: Dictionary) => customer.id === item.id,
        )
        const serialNumber = (currentPage - 1) * pageSize + itemIndex + 1
        return <span className='text-sm text-gray-600'>{serialNumber}</span>
      },
    },
    {
      key: 'name',
      header: 'Name',
      accessor: 'name',
      sortable: false,
      width: '250px',
      render: (_, item) => {
        return (
          <AvatarNameComp
            src={item?.avatar}
            name={item.name || `${item.first_name} ${item.last_name}`}
            showIndicator={true}
            indicatorColor={item.status === 'active' ? 'green' : 'yellow'}
          />
        )
      },
    },
    {
      key: 'total_orders',
      header: 'Total Orders',
      accessor: 'total_orders',
      sortable: false,
      width: '120px',
      render: (_, item) => {
        return (
          <span className='text-sm text-gray-900'>
            {item.total_orders || 0}
          </span>
        )
      },
    },
    {
      key: 'amount_spent',
      header: 'Amount Spent',
      accessor: 'amount_spent',
      sortable: false,
      width: '140px',
      render: (_, item) => {
        const amount = parseFloat(item.amount_spent || '0')
        const formatted = new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount)
        return <span className='text-sm text-gray-900'>{formatted}</span>
      },
    },
    {
      key: 'location',
      header: 'Location',
      accessor: 'location',
      sortable: false,
      width: '120px',
      render: (_, item) => {
        return (
          <span className='text-sm text-gray-600'>
            {item.location === 'N/A' || !item.location ? 'N/A' : item.location}
          </span>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: false,
      width: '100px',
      render: (value) => <StatusBadge key={value} status={value} />,
    },
  ]

  const actions: TableAction<Dictionary>[] = [
    {
      label: 'View Details',
      onClick: (item) => {
        navigate(`${CUSTOMERS}/${item.id}`)
      },
    },
  ]

  const { data: customerFilters, isLoading: isFiltersLoading } =
    useGetAdminFiltersQuery({ table: 'customers' })

  const { data, isLoading, isFetching } = useGetCustomerListQuery(
    {
      search: searchQuery,
      location: filterQuery.location,
      status: filterQuery.status,
      sort_by: filterQuery.sort_by,
      sort_order: filterQuery.sort_order,
      per_page: pageSize,
      page: currentPage,
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  )

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const debouncedSearch = useDebounce(handleSearch, 1000)

  return (
    <section className='w-full overflow-hidden my-5'>
      <div className='rounded-lg bg-card p-2 h-[calc(100vh-120px)] custom_scroll'>
        <ReusableTable
          title='Customer List'
          titleIcon={OrderTableSvgIcon}
          data={data?.customers || []}
          columns={columns}
          actions={actions}
          searchable={true}
          searchPlaceholder='Search customers...'
          filters={[
            {
              key: 'status',
              label: 'Status',
              loading: isFiltersLoading,
              options: isFiltersLoading
                ? []
                : [
                    ...(customerFilters?.statuses?.map((status: any) => ({
                      label: status.label || status.name || status,
                      value: status.value || status,
                    })) || []),
                  ],
            },
          ]}
          pagination={true}
          pageSize={pageSize}
          externalPagination={true}
          currentPage={data?.pagination?.current_page || 1}
          totalPages={data?.pagination?.last_page || 1}
          totalItems={data?.pagination?.total || 0}
          onPageChange={(page) => {
            setCurrentPage(page)
          }}
          height='600px'
          emptyStateTitle='No customers found'
          emptyStateDescription='No customers match your current search and filter criteria.'
          loading={isLoading || isFetching}
          handleSearchItem={(value) => {
            debouncedSearch(value)
          }}
          handleFilterItem={(data: Dictionary) => {
            setFilterQuery((prev) => ({
              ...prev,
              location:
                data.location === 'all' ? '' : data.location || prev.location,
              status: data.status === 'all' ? '' : data.status || prev.status,
            }))
            setCurrentPage(1) // Reset to first page when filtering
          }}
          className='h-[calc(100vh-120px)] overflow-y-auto custom_scroll'
        />
      </div>
    </section>
  )
}
